/**
 * PSLE cut-off point section for school pages.
 *
 * What: Renders MOE's posting-group cut-off table for one school, the footnotes
 *       it has to be read with, its dated provenance, and the bridge into the
 *       Mathematics syllabus each posting group leads to.
 * Why:  The page ranks for '<school> cut off point' and the number was nowhere
 *       on it. The bridge is what makes the section useful to a parent rather
 *       than SEO decoration.
 * When: Update when the cut-off content shape changes; the figures themselves
 *       come from `schoolCutOffContent.ts` and never from this file.
 *
 * Rendered as a real `<table>` so the figures are extractable by crawlers and
 * navigable by a screen reader.
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Paper, Stack, Typography } from '@mui/material';

import LandingSectionShell from '@/components/landing/LandingSectionShell';
import { colors, radii, shadows } from '@/components/landing/landingTokens';
import type {
  SchoolCutOffCell,
  SchoolCutOffContent,
} from '@/content/schoolCutOffContent';

/**
 * Anchor id, for the contents nav.
 *
 * Not a scroll-reveal key. This table is the figure most of the page's traffic
 * searched for, so it paints on arrival rather than animating in — see the note
 * above `support` in `sectionRevealMap`.
 */
export const SCHOOL_CUT_OFF_SECTION_ID = 'school-cut-off';

interface SchoolCutOffSectionProps {
  content: SchoolCutOffContent;
}

const cellSx = {
  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
  color: colors.ink,
  textAlign: 'left' as const,
  verticalAlign: 'top' as const,
  padding: '10px 12px 10px 0',
  borderBottom: `1px solid ${colors.border}`,
};

const headerCellSx = {
  ...cellSx,
  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
  fontWeight: 700,
  color: colors.inkSubtle,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
};

/** A single figure plus whatever caveat MOE attaches to it. */
function CutOffValue({ cell }: { cell?: SchoolCutOffCell }) {
  // MOE publishes no figure for a posting group the school takes no intake
  // through. An em dash says that without implying a zero.
  if (!cell) {
    return (
      <Typography component="span" sx={{ color: colors.inkSubtle }}>
        —
      </Typography>
    );
  }

  return (
    <Stack spacing={0.25}>
      <Typography
        component="span"
        sx={{ fontWeight: 700, color: colors.ink, whiteSpace: 'nowrap' }}
      >
        {cell.range}
        {cell.vacancyFloor && (
          <Typography
            component="span"
            aria-label="see footnote"
            sx={{ fontWeight: 700, color: colors.accentCobalt }}
          >
            *
          </Typography>
        )}
      </Typography>
      {cell.note && (
        <Typography
          component="span"
          sx={{ fontSize: '0.75rem', color: colors.inkMuted, lineHeight: 1.4 }}
        >
          {cell.note}
        </Typography>
      )}
    </Stack>
  );
}

function SchoolCutOffSection({ content }: SchoolCutOffSectionProps) {
  const titleId = 'school-cut-off-title';
  const { columns, rows, bridge } = content;

  return (
    <LandingSectionShell
      id={SCHOOL_CUT_OFF_SECTION_ID}
      ariaLabelledby={titleId}
      // Spacing between these sections comes from the page-level stack.
      mb={0}
      sx={{ width: '100%' }}
    >
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${colors.border}`,
          borderRadius: radii.md,
          backgroundColor: colors.paper,
          boxShadow: shadows.soft,
          p: { xs: 2.5, sm: 3, md: 4 },
        }}
      >
        <Stack spacing={{ xs: 2, sm: 2.5 }}>
          <Typography
            id={titleId}
            component="h2"
            sx={{
              fontSize: { xs: '1.375rem', sm: '1.5rem', md: '1.625rem' },
              fontWeight: 800,
              color: colors.ink,
              lineHeight: 1.25,
              letterSpacing: '-0.02em',
            }}
          >
            {content.heading}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '0.9375rem', sm: '1rem' },
              color: colors.inkMuted,
              lineHeight: 1.65,
            }}
          >
            {content.intro}
          </Typography>

          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: columns.affiliated ? 380 : 260,
              }}
            >
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" scope="col" sx={headerCellSx}>
                    {columns.group}
                  </Box>
                  <Box component="th" scope="col" sx={headerCellSx}>
                    {columns.nonAffiliated}
                  </Box>
                  {columns.affiliated && (
                    <Box component="th" scope="col" sx={headerCellSx}>
                      {columns.affiliated}
                    </Box>
                  )}
                </Box>
              </Box>
              <Box component="tbody">
                {rows.map(row => (
                  <Box component="tr" key={row.key}>
                    <Box
                      component="th"
                      scope="row"
                      sx={{ ...cellSx, fontWeight: 600 }}
                    >
                      {row.label}
                    </Box>
                    <Box component="td" sx={cellSx}>
                      <CutOffValue cell={row.nonAffiliated} />
                    </Box>
                    {columns.affiliated && (
                      <Box component="td" sx={cellSx}>
                        <CutOffValue cell={row.affiliated} />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Stack spacing={0.75}>
            {content.footnotes.map(footnote => (
              <Typography
                key={footnote}
                sx={{
                  fontSize: '0.8125rem',
                  color: colors.inkMuted,
                  lineHeight: 1.55,
                }}
              >
                {footnote}
              </Typography>
            ))}
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: colors.inkMuted,
                lineHeight: 1.55,
              }}
            >
              {content.caveat}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: colors.inkSubtle,
                lineHeight: 1.55,
              }}
            >
              {content.vintageLine}{' '}
              <a
                href={content.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: colors.accentCobalt, fontWeight: 600 }}
              >
                {content.sourceLabel} ↗
              </a>
            </Typography>
          </Stack>

          <Box
            sx={{
              borderTop: `1px solid ${colors.border}`,
              pt: { xs: 2, sm: 2.5 },
            }}
          >
            <Stack spacing={{ xs: 1.5, sm: 2 }}>
              <Typography
                component="h3"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.0625rem' },
                  fontWeight: 700,
                  color: colors.ink,
                }}
              >
                {bridge.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.9375rem', sm: '1rem' },
                  color: colors.inkMuted,
                  lineHeight: 1.65,
                }}
              >
                {bridge.body}
              </Typography>
              <Stack component="ul" spacing={1.25} sx={{ m: 0, pl: 0 }}>
                {bridge.links.map(link => (
                  <Box
                    component="li"
                    key={link.path}
                    sx={{ listStyle: 'none', m: 0 }}
                  >
                    <RouterLink
                      to={link.path}
                      style={{
                        color: colors.accentCobalt,
                        fontWeight: 700,
                        textDecoration: 'none',
                        fontSize: '0.9375rem',
                      }}
                    >
                      {link.label}
                    </RouterLink>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: colors.inkMuted,
                        lineHeight: 1.55,
                      }}
                    >
                      {link.description}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </LandingSectionShell>
  );
}

export default SchoolCutOffSection;
